import { scanForbiddenData } from "./analytics-forbidden-data.js";
import { conciergeRequestStates } from "./concierge-request.js";
import { FEATURE_FLAGS, featureFlagKeys } from "./feature-flag-registry.js";
import { handoffReasonCodes, handoffRequesters } from "./handoff.js";
import { staffRoles } from "./staff.js";
// The product analytics taxonomy (CHT-769, Phase 13.3). A CLOSED, VERSIONED list of events. Every event names its properties,
// and every property is one of seven kinds that cannot carry free text: a closed enum, a bucket label, a flag, a small integer, an
// opaque id, a release tag or a calendar month. Each kind has a strict format. There is no "string" kind. So a message, a memory, a name or a note has no way into an event, even by mistake.
//
// Rules of the taxonomy:
// - A change of the list (an event, a property or a value) raises `ANALYTICS_TAXONOMY_VERSION`. The version is sent with every event.
// - The customer is never a property. The customer is the `distinct_id`, a salted hash made by the port (never the raw id).
// - Money is a bucket label. Raw amounts never leave the server, with or without a customer id.
// - `provider_mode` (`fake` or `real`) is required on every payment event, so a FAKE gateway result is never read as launch evidence.
// - The payment events are emitted by the payments lane (`emittedBy: "payments_lane"`). This lane defines the names and nothing else.
// Version 2 (CHT-2028) adds the visit events (`session_started`, `screen_viewed`, `session_ended`) and the optional `visit_id` and `visit_seq` of
// three existing client events. It only adds: no event, property or value of version 1 changed.
export const ANALYTICS_TAXONOMY_VERSION = 2;
export const analyticsSurfaces = Object.freeze(["server", "mobile", "admin"]);
export const analyticsProviderModes = Object.freeze(["fake", "real"]);
const ID = { kind: "entity_id" };
const FLAG = { kind: "flag" };
const RELEASE = { kind: "release_tag" };
const enumOf = (...values) => ({ kind: "enum", values });
const bucketOf = (...labels) => ({ kind: "bucket", labels });
/** The money buckets, in paise of INR. A label says a range, never an amount. */
export const AMOUNT_BUCKET_LABELS = Object.freeze([
    "lt_5k",
    "5k_to_25k",
    "25k_to_100k",
    "100k_to_500k",
    "gte_500k",
]);
export const AMOUNT_BUCKET_EDGES_PAISE = Object.freeze([
    500_000, 2_500_000, 10_000_000, 50_000_000,
]);
/** The duration buckets, in seconds. */
export const DURATION_BUCKET_LABELS = Object.freeze([
    "lt_1m",
    "1m_to_5m",
    "5m_to_30m",
    "30m_to_4h",
    "gte_4h",
]);
export const DURATION_BUCKET_EDGES_SECONDS = Object.freeze([60, 300, 1_800, 14_400]);
/** The count buckets: how many (items, tool calls, results). */
export const COUNT_BUCKET_LABELS = Object.freeze(["0", "1", "2_to_3", "4_to_9", "gte_10"]);
export const COUNT_BUCKET_EDGES = Object.freeze([1, 2, 4, 10]);
/** The label of `value` for ascending `edges`: `labels[0]` below `edges[0]`, and so on. A negative or non-finite value is the first label. */
function bucketLabel(value, edges, labels) {
    const safe = Number.isFinite(value) && value > 0 ? value : 0;
    const index = edges.findIndex((edge) => safe < edge);
    const label = labels[index === -1 ? labels.length - 1 : index];
    if (label === undefined)
        throw new RangeError("bucket labels and edges do not match");
    return label;
}
export const amountBucketOfPaise = (paise) => bucketLabel(paise, AMOUNT_BUCKET_EDGES_PAISE, AMOUNT_BUCKET_LABELS);
export const durationBucketOfSeconds = (seconds) => bucketLabel(seconds, DURATION_BUCKET_EDGES_SECONDS, DURATION_BUCKET_LABELS);
export const countBucketOf = (count) => bucketLabel(count, COUNT_BUCKET_EDGES, COUNT_BUCKET_LABELS);
const AMOUNT_BUCKET = bucketOf(...AMOUNT_BUCKET_LABELS);
const DURATION_BUCKET = bucketOf(...DURATION_BUCKET_LABELS);
const COUNT_BUCKET = bucketOf(...COUNT_BUCKET_LABELS);
/** The ways the phone app comes to the foreground. The same list for `app_opened.entry` and `session_started.entry`. */
export const APP_ENTRY_VALUES = Object.freeze([
    "cold_start",
    "resume",
    "deep_link",
    "notification",
]);
export const APP_PLATFORM_VALUES = Object.freeze(["ios", "android"]);
/**
 * The closed list of phone screens (CHT-2028). The phone maps an Expo Router route to one of these names, never to a path: a path may hold an
 * id. A route that is not listed is `other`.
 */
export const ANALYTICS_SCREEN_NAMES = Object.freeze([
    "concierge",
    "trips",
    "trip_detail",
    "trip_new",
    "trip_edit",
    "itinerary_add",
    "itinerary_review",
    "itinerary_edit",
    "actions",
    "action_detail",
    "saved",
    "saved_item",
    "profile",
    "memory",
    "request_new",
    "request_answer",
    "sign_in",
    "other",
]);
export const VISIT_END_REASONS = Object.freeze(["background", "idle_timeout", "sign_out"]);
/** `visit_seq` of the first event of a visit (`session_started`) is 0; every later event adds 1. At most this value. */
export const VISIT_SEQ_MAX = 100_000;
const VISIT_ID = ID;
const VISIT_SEQ = { kind: "int", min: 0, max: VISIT_SEQ_MAX };
const SCREEN = enumOf(...ANALYTICS_SCREEN_NAMES);
const ENTRY = enumOf(...APP_ENTRY_VALUES);
const PAYMENT_PROVIDER_MODE = enumOf(...analyticsProviderModes);
const PAYMENT_FAILURE = enumOf("DECLINED", "EXPIRED", "CANCELLED", "AMOUNT_MISMATCH", "VERIFICATION_FAILED", "OTHER");
const paymentEvent = (extra = {}) => ({
    emittedBy: "payments_lane",
    surfaces: ["server"],
    required: ["payment_request_id", "provider_mode", "amount_bucket", "currency"],
    properties: {
        payment_request_id: ID,
        request_id: ID,
        provider_mode: PAYMENT_PROVIDER_MODE,
        amount_bucket: AMOUNT_BUCKET,
        currency: enumOf("INR"),
        ...extra,
    },
});
export const ANALYTICS_EVENTS = {
    // --- activation and core usage -----------------------------------------------------------------------------------------
    app_opened: {
        emittedBy: "client",
        surfaces: ["mobile"],
        required: ["entry"],
        properties: { entry: ENTRY, visit_id: VISIT_ID, visit_seq: VISIT_SEQ },
    },
    // --- visits of the phone app (CHT-2028, ADR 0017) ----------------------------------------------------------------------
    session_started: {
        emittedBy: "client",
        surfaces: ["mobile"],
        required: ["visit_id", "visit_seq", "entry", "platform"],
        properties: {
            visit_id: VISIT_ID,
            visit_seq: VISIT_SEQ,
            entry: ENTRY,
            platform: enumOf(...APP_PLATFORM_VALUES),
        },
    },
    screen_viewed: {
        emittedBy: "client",
        surfaces: ["mobile"],
        required: ["visit_id", "visit_seq", "screen", "previous"],
        properties: {
            visit_id: VISIT_ID,
            visit_seq: VISIT_SEQ,
            screen: SCREEN,
            previous: enumOf("none", ...ANALYTICS_SCREEN_NAMES),
        },
    },
    session_ended: {
        emittedBy: "client",
        surfaces: ["mobile"],
        required: ["visit_id", "visit_seq", "end_reason", "duration_bucket", "screens_bucket"],
        properties: {
            visit_id: VISIT_ID,
            visit_seq: VISIT_SEQ,
            end_reason: enumOf(...VISIT_END_REASONS),
            duration_bucket: DURATION_BUCKET,
            screens_bucket: COUNT_BUCKET,
        },
    },
    activation_step_completed: {
        emittedBy: "server",
        surfaces: ["server"],
        required: ["step"],
        properties: {
            step: enumOf("profile_completed", "first_conversation", "first_save", "first_request"),
        },
    },
    concierge_run_completed: {
        emittedBy: "server",
        surfaces: ["server"],
        required: ["outcome"],
        properties: {
            outcome: enumOf("COMPLETED", "DEGRADED", "FAILED"),
            latency_bucket: DURATION_BUCKET,
            tool_call_count_bucket: COUNT_BUCKET,
            prompt_version: RELEASE,
            registry_version: RELEASE,
        },
    },
    recommendation_viewed: {
        emittedBy: "client",
        surfaces: ["mobile"],
        required: ["card_kind"],
        properties: {
            card_kind: enumOf("catalog_item", "itinerary", "request_update", "action"),
            catalog_item_id: ID,
            position_bucket: COUNT_BUCKET,
            visit_id: VISIT_ID,
            visit_seq: VISIT_SEQ,
        },
    },
    // --- save --------------------------------------------------------------------------------------------------------------
    save_added: {
        emittedBy: "server",
        surfaces: ["server"],
        required: ["catalog_item_id"],
        properties: { catalog_item_id: ID, source: enumOf("chat", "card", "itinerary", "browse") },
    },
    save_removed: {
        emittedBy: "server",
        surfaces: ["server"],
        required: ["catalog_item_id"],
        properties: { catalog_item_id: ID },
    },
    // --- request -----------------------------------------------------------------------------------------------------------
    request_created: {
        emittedBy: "server",
        surfaces: ["server"],
        // `source` is optional: the committed receipt of a request command does not carry where it began, and an event must reflect that
        // canonical state, not a guess. Only a staff-made request names its source.
        required: ["request_id"],
        properties: {
            request_id: ID,
            source: enumOf("chat", "card", "itinerary", "staff"),
            has_catalog_item: FLAG,
        },
    },
    request_state_changed: {
        emittedBy: "server",
        surfaces: ["server"],
        required: ["request_id", "to_state"],
        properties: {
            request_id: ID,
            from_state: enumOf(...conciergeRequestStates),
            to_state: enumOf(...conciergeRequestStates),
            time_in_state_bucket: DURATION_BUCKET,
        },
    },
    // --- itinerary ---------------------------------------------------------------------------------------------------------
    itinerary_created: {
        emittedBy: "server",
        surfaces: ["server"],
        required: ["itinerary_id", "origin"],
        properties: { itinerary_id: ID, origin: enumOf("customer", "staff", "agent") },
    },
    itinerary_edited: {
        emittedBy: "server",
        surfaces: ["server"],
        required: ["itinerary_id", "edit_kind"],
        properties: {
            itinerary_id: ID,
            edit_kind: enumOf("add_item", "move_item", "remove_item", "retime_item", "other"),
            item_count_bucket: COUNT_BUCKET,
        },
    },
    // --- handoff -----------------------------------------------------------------------------------------------------------
    handoff_requested: {
        emittedBy: "server",
        surfaces: ["server"],
        required: ["handoff_id", "reason"],
        properties: {
            handoff_id: ID,
            reason: enumOf(...handoffReasonCodes),
            requester: enumOf(...handoffRequesters),
        },
    },
    handoff_accepted: {
        emittedBy: "server",
        surfaces: ["server"],
        required: ["handoff_id"],
        properties: { handoff_id: ID, time_to_accept_bucket: DURATION_BUCKET },
    },
    // --- action and notification -------------------------------------------------------------------------------------------
    action_completed: {
        emittedBy: "server",
        surfaces: ["server"],
        required: ["action_kind"],
        properties: {
            action_kind: enumOf("answer_question", "approve_quote", "confirm_itinerary", "other"),
            action_id: ID,
        },
    },
    notification_opened: {
        emittedBy: "client",
        surfaces: ["mobile"],
        required: ["topic"],
        properties: {
            topic: enumOf("request_update", "handoff", "action_needed", "trip_inspiration", "product_news"),
            visit_id: VISIT_ID,
            visit_seq: VISIT_SEQ,
        },
    },
    // --- admin operations --------------------------------------------------------------------------------------------------
    admin_operation_performed: {
        emittedBy: "client",
        surfaces: ["admin"],
        required: ["operation"],
        properties: {
            operation: enumOf("request_assigned", "handoff_accepted", "catalog_published", "import_approved", "memory_reviewed", "itinerary_validated"),
            staff_role: enumOf(...staffRoles),
        },
    },
    // --- experiments -------------------------------------------------------------------------------------------------------
    experiment_exposed: {
        emittedBy: "server",
        surfaces: ["server"],
        required: ["flag_key", "variant"],
        // The registry is the closed list of both. A pair that the registry does not list is refused (see `crossCheck`).
        properties: {
            flag_key: enumOf(...featureFlagKeys),
            variant: enumOf(...new Set(featureFlagKeys.flatMap((key) => [...FEATURE_FLAGS[key].variants]))),
        },
    },
    // --- payment funnel (EMITTED BY THE PAYMENTS LANE; defined here, never edited into payment code by this lane) ------------
    payment_requested: paymentEvent(),
    payment_started: paymentEvent(),
    payment_succeeded: paymentEvent(),
    payment_failed: paymentEvent({ failure_code: PAYMENT_FAILURE }),
    payment_expired: paymentEvent(),
    payment_refunded: paymentEvent(),
};
export const analyticsEventNames = Object.freeze(Object.keys(ANALYTICS_EVENTS));
/** The events a client (mobile or admin) may send through the ingest route. Every other event is server only. */
export const analyticsClientEventNames = Object.freeze(analyticsEventNames.filter((name) => ANALYTICS_EVENTS[name].emittedBy === "client"));
export const isAnalyticsEventName = (value) => Object.hasOwn(ANALYTICS_EVENTS, value);
/**
 * The properties the service adds to EVERY event (version, environment, build context). A caller never sends them. They have the same
 * strict kinds as the rest, so `app_version` and `build_id` are release tags, never free text.
 */
export const ANALYTICS_CONTEXT_KEYS = Object.freeze([
    "taxonomy_version",
    "environment",
    "surface",
    "app_version",
    "build_id",
]);
export const analyticsEnvironments = Object.freeze([
    "development",
    "test",
    "staging",
    "production",
]);
export const ANALYTICS_CONTEXT_SPECS = {
    taxonomy_version: { kind: "int", min: 1, max: 1_000 },
    environment: enumOf(...analyticsEnvironments),
    surface: enumOf(...analyticsSurfaces),
    app_version: RELEASE,
    build_id: RELEASE,
};
/**
 * The user properties a pseudonymous profile may carry. A closed list, with the same five kinds as an event property. Nothing
 * about the person: no name, no contact, no home city. `signup_month` is a calendar month, not a day.
 */
export const ANALYTICS_USER_PROPERTIES = {
    platform: enumOf("ios", "android", "web"),
    app_version: RELEASE,
    locale_family: enumOf("en", "hi", "other"),
    signup_month: { kind: "month" },
};
/**
 * EVERY key that any event, the context or the user properties may hold. A violation or a log names a property only if it is one of
 * these: a key that a caller made up is never repeated (it may be an email or a token).
 */
export const ANALYTICS_ALLOWED_KEYS = new Set([
    ...Object.values(ANALYTICS_EVENTS).flatMap((spec) => Object.keys(spec.properties)),
    ...Object.keys(ANALYTICS_CONTEXT_SPECS),
    ...Object.keys(ANALYTICS_USER_PROPERTIES),
]);
const safeProperty = (key) => (ANALYTICS_ALLOWED_KEYS.has(key) ? key : null);
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;
/**
 * The release tags: `local`, `unknown`, a build number (up to 10 digits), `v` and 1 to 3 digits, a dotted version (`1.2`, `1.2.3.4`, with an
 * optional `-rc1` style suffix) or a short hash (7 to 12 hex characters). A word such as a name does not fit.
 */
export const RELEASE_TAG = /^(?:local|unknown|[0-9]{1,10}|v[0-9]{1,3}|[0-9]{1,4}(?:\.[0-9]{1,4}){1,3}(?:-[a-z0-9]{1,12})?|[a-f0-9]{7,12})$/;
export const isReleaseTag = (value) => RELEASE_TAG.test(value);
const MONTH = /^20[0-9]{2}-(?:0[1-9]|1[0-2])$/;
function valueFits(spec, value) {
    switch (spec.kind) {
        case "enum":
            return typeof value === "string" && spec.values.includes(value);
        case "bucket":
            return typeof value === "string" && spec.labels.includes(value);
        case "flag":
            return typeof value === "boolean";
        case "int":
            return (typeof value === "number" &&
                Number.isInteger(value) &&
                value >= spec.min &&
                value <= spec.max);
        case "entity_id":
            return typeof value === "string" && UUID.test(value);
        case "release_tag":
            return typeof value === "string" && RELEASE_TAG.test(value);
        case "month":
            return typeof value === "string" && MONTH.test(value);
    }
}
/** A UUID version 4, the shape that the phone makes for a visit id (`crypto.randomUUID`). */
const UUID_V4 = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/;
/**
 * The rules of a phone visit (CHT-2028). The visit id is a UUID v4 (the phone makes it with `crypto.randomUUID`): a uuid of another version is
 * not a visit id. The visit id and the sequence go TOGETHER: both present or both absent. An event with one of them would reach the vendor and
 * could never be counted in a visit, so it is refused. (An event that REQUIRES both already reports the missing one; the caller drops the repeat.)
 */
function visitChecks(properties) {
    const violations = [];
    if (typeof properties.visit_id === "string" && !UUID_V4.test(properties.visit_id)) {
        violations.push({ code: "INVALID_VALUE", property: "visit_id" });
    }
    const hasId = properties.visit_id !== undefined;
    const hasSeq = properties.visit_seq !== undefined;
    if (hasId && !hasSeq)
        violations.push({ code: "MISSING_PROPERTY", property: "visit_seq" });
    if (hasSeq && !hasId)
        violations.push({ code: "MISSING_PROPERTY", property: "visit_id" });
    return violations;
}
/** A rule that spans two properties. The pair of an exposure must be a flag and a variant that the registry lists together. */
function crossCheck(event, properties) {
    const violations = visitChecks(properties);
    if (event !== "experiment_exposed")
        return violations;
    const key = properties.flag_key;
    const variant = properties.variant;
    if (typeof key !== "string" || typeof variant !== "string")
        return violations;
    const variants = Object.hasOwn(FEATURE_FLAGS, key)
        ? FEATURE_FLAGS[key].variants
        : [];
    if (!variants.includes(variant))
        violations.push({ code: "INVALID_VALUE", property: "variant" });
    return violations;
}
/** Adds the violations that the list does not hold yet. A rule may name a violation that the key loop already found (a missing required key): it is reported once. */
function appendNew(into, found) {
    for (const violation of found) {
        if (!into.some((have) => have.code === violation.code && have.property === violation.property)) {
            into.push(violation);
        }
    }
}
function validateAgainst(specs, required, input, event = null) {
    const violations = [];
    const properties = {};
    for (const [key, value] of Object.entries(input)) {
        if (value === undefined)
            continue;
        const spec = Object.hasOwn(specs, key) ? specs[key] : undefined;
        if (spec === undefined) {
            // The key is the caller's: it is NOT repeated unless the taxonomy knows it.
            violations.push({ code: "UNKNOWN_PROPERTY", property: safeProperty(key) });
        }
        else if (!valueFits(spec, value)) {
            violations.push({ code: "INVALID_VALUE", property: key });
        }
        else {
            properties[key] = value;
        }
    }
    for (const key of required) {
        if (!Object.hasOwn(properties, key) && !violations.some((entry) => entry.property === key)) {
            violations.push({ code: "MISSING_PROPERTY", property: key });
        }
    }
    appendNew(violations, crossCheck(event, properties));
    // The second wall: the scanner reads every key and value again, whatever the spec said. Its allowlist is the spec of THIS field set.
    for (const finding of scanForbiddenData(properties, new Set(Object.keys(specs)))) {
        violations.push({ code: "FORBIDDEN_DATA", property: finding.path });
    }
    return violations.length === 0 ? { ok: true, properties } : { ok: false, violations };
}
/**
 * Checks the properties of one event against the taxonomy. `surface` is the emitting side: a client may only send a `client` event
 * from a surface the event lists. The result holds the accepted properties only, or the violations (rule and key, never a value).
 */
export function validateAnalyticsEvent(name, input, options = {}) {
    if (!isAnalyticsEventName(name)) {
        return { ok: false, violations: [{ code: "UNKNOWN_EVENT", property: null }] };
    }
    const spec = ANALYTICS_EVENTS[name];
    if (options.surface !== undefined) {
        const fromClient = options.surface !== "server";
        if (!spec.surfaces.includes(options.surface) || (fromClient && spec.emittedBy !== "client")) {
            return { ok: false, violations: [{ code: "NOT_FROM_THIS_SURFACE", property: null }] };
        }
    }
    return validateAgainst(spec.properties, spec.required, input, name);
}
/** Checks a user-property bag against the closed list of user properties. */
export function validateAnalyticsUserProperties(input) {
    return validateAgainst(ANALYTICS_USER_PROPERTIES, [], input);
}
/**
 * The check at EVERY emit boundary (the service, the fake port, the vendor adapter): a finished payload must hold exactly the
 * properties of its event plus the five context properties, each with its strict format, and the context must be complete. A user-property
 * payload (`event` null) must fit the closed user-property list. A bucket is a label of the list, never a number; a month is `YYYY-MM`; a
 * release tag has a fixed shape. This is the field-specific allowlist: a key or a value that is not listed is refused, whatever its name.
 */
export function validateEnvelopeProperties(event, properties) {
    if (event === null)
        return validateAnalyticsUserProperties(properties);
    if (!isAnalyticsEventName(event)) {
        return { ok: false, violations: [{ code: "UNKNOWN_EVENT", property: null }] };
    }
    const spec = ANALYTICS_EVENTS[event];
    return validateAgainst({ ...spec.properties, ...ANALYTICS_CONTEXT_SPECS }, [...spec.required, ...ANALYTICS_CONTEXT_KEYS], properties, event);
}
